import React, { useState } from "react";
import { MapPin, Search } from "lucide-react";
import {
  Cutlery,
  Healthcare,
  NavArrowLeft,
  ShoppingBag,
  Star,
} from "iconoir-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { mockData, mockTabs } from "@/lib/mock-data/places";
import { useResponsive } from "@/hooks/useResponsive";
import { FeaturedPlace } from "./FeaturedPlace";
import { SubcategorySection } from "./SubcategorySection";

export const FavoritePlacesList: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState("Tất cả");
  const navigate = useNavigate();
  const {
    isIphoneSE,
    isIphoneXR,
    isIphone12,
    isIphone14ProMax,
    isSamsungGalaxy,
  } = useResponsive();

  // Tính toán padding và kích thước dựa trên loại thiết bị
  const getDeviceSpecificStyles = () => {
    if (isIphoneSE || isSamsungGalaxy) {
      return {
        containerPadding: "px-2 py-1",
        itemPadding: "p-2",
        imageSize: "h-40",
        fontSize: "text-xs",
        iconSize: "w-10 h-10",
        gap: "gap-1",
      };
    }
    if (isIphone12 || isIphoneXR) {
      return {
        containerPadding: "px-3 py-2",
        itemPadding: "p-3",
        imageSize: "h-44",
        fontSize: "text-sm",
        iconSize: "w-12 h-12",
        gap: "gap-2",
      };
    }
    if (isIphone14ProMax) {
      return {
        containerPadding: "px-4 py-3",
        itemPadding: "p-4",
        imageSize: "h-48",
        fontSize: "text-base",
        iconSize: "w-14 h-14",
        gap: "gap-3",
      };
    }

    // Default styles
    return {
      containerPadding: "px-3 py-2",
      itemPadding: "p-3",
      imageSize: "h-44",
      fontSize: "text-sm",
      iconSize: "w-12 h-12",
      gap: "gap-2",
    };
  };

  const styles = getDeviceSpecificStyles();

  return (
    <div className="w-full h-full bg-[#FBF6F1] flex flex-col">
      {/* Header */}
      <div
        className={`w-full h-14 ${styles.containerPadding} inline-flex justify-between items-center`}
      >
        <div
          className="size-7 relative overflow-hidden"
          onClick={() => navigate(-1)}
        >
          <NavArrowLeft className="h-6 w-6 text-[#1E3A3A]" />
        </div>
        <div
          className={`justify-start text-[#1E3A3A] text-xl font-medium font-manrope leading-7`}
        >
          Địa điểm yêu thích
        </div>
        <div className="size-7 relative opacity-0 overflow-hidden">
          <div className="size-5 left-[4.58px] top-[4.18px] absolute outline outline-[1.20px] outline-offset-[-0.60px] outline-icon-primary" />
        </div>
      </div>

      {/* Search */}
      <div
        className={`self-stretch ${styles.containerPadding} flex flex-col justify-start items-start ${styles.gap}`}
      >
        <div
          className={`self-stretch p-3 bg-white rounded-md inline-flex justify-start items-center ${styles.gap}`}
        >
          <div className="size-6 relative overflow-hidden">
            <Search className="h-5 w-5 text-[#6F6F6B]" />
          </div>
          <input
            type="text"
            placeholder="Tìm kiếm..."
            className={`bg-transparent border-none outline-none ${styles.fontSize} w-full text-[#6F6F6B] font-normal font-sora leading-none`}
          />
        </div>
      </div>

      {/* Location */}
      <div
        className={`self-stretch ${styles.containerPadding} inline-flex justify-center items-center ${styles.gap}`}
      >
        <div className="size-4 relative overflow-hidden">
          <MapPin className="h-4 w-4 text-[#6F6F6B]" />
        </div>
        <div
          className={`justify-start text-[#6F6F6B] ${styles.fontSize} font-normal font-sora leading-none`}
        >
          Lò Đúc, Hai Bà Trưng, Hà Nội
        </div>
      </div>

      {/* Map */}
      <div className={`w-full ${styles.imageSize} bg-zinc-300`} />

      {/* Tabs */}
      <div className={`self-stretch ${styles.containerPadding}`}>
        <div className={`flex flex-wrap items-center flex-wrap gap-2 py-4`}>
          {mockTabs.map((tab) => (
            <div
              key={tab.label}
              className={cn(
                `px-4 py-4 rounded-[6px] border border-1`,
                selectedTab === tab.label
                  ? "border-[#E06448] bg-[#FCE8E4] text-[#E06448]"
                  : "border-[#F4E7DA] text-[#1E3A3A]",
              )}
              onClick={() => setSelectedTab(tab.label)}
            >
              <div
                className={`text-center ${styles.fontSize} font-normal font-sora leading-none`}
              >
                {tab.label}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Content */}
      <div
        className={`self-stretch ${styles.containerPadding} flex flex-col justify-center items-center ${styles.gap}`}
      >
        {/* Filtered data based on selected tab */}
        {mockData
          .filter(
            (data) => selectedTab === "Tất cả" || data.category === selectedTab,
          )
          .map((data, index) => (
            <React.Fragment key={index}>
              {/* Category Header - Only show in "Tất cả" tab */}
              {selectedTab === "Tất cả" && (
                <div
                  className={`self-stretch inline-flex justify-start items-center ${styles.gap} mb-1`}
                >
                  <div className="size-6 relative overflow-hidden flex-shrink-0">
                    {data.category === "Ăn uống" && (
                      <Cutlery className="h-6 w-6 text-[#1E3A3A]" />
                    )}
                    {data.category === "Dịch vụ" && (
                      <Star className="h-6 w-6 text-[#1E3A3A]" />
                    )}
                    {data.category === "Sức khỏe" && (
                      <Healthcare className="h-6 w-6 text-[#1E3A3A]" />
                    )}
                    {data.category === "Mua sắm" && (
                      <ShoppingBag className="h-6 w-6 text-[#1E3A3A]" />
                    )}
                  </div>
                  <div
                    className={`text-[#1E3A3A] text-lg font-semibold font-manrope leading-normal`}
                  >
                    {data.category}
                  </div>
                </div>
              )}

              {/* Featured Item - Only show if featured exists */}
              {data.featured && (
                <FeaturedPlace featured={data.featured} styles={styles} />
              )}

              {/* Subcategories - Special rendering for Mua sắm, Sức khỏe, and Dịch vụ nhà cửa */}
              {data.category === "Mua sắm" ||
              data.category === "Sức khỏe" ||
              data.category === "Dịch vụ nhà cửa" ? (
                <>
                  {data.subcategories.map((subcategory, subIndex) => (
                    <SubcategorySection
                      key={subIndex}
                      subcategory={subcategory}
                      styles={styles}
                    />
                  ))}
                </>
              ) : (
                <>
                  {/* Standard subcategories for other categories */}
                  {data.subcategories.map((subcategory, subIndex) => (
                    <SubcategorySection
                      key={subIndex}
                      subcategory={subcategory}
                      styles={styles}
                    />
                  ))}
                </>
              )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};
